/**
 * Line chart component built with D3.js v7.
 *
 * Supports multi-series lines with gradient area fills, draw-in animation
 * via stroke-dashoffset, interactive data point dots, and axis/grid lines.
 */

import { useRef, useEffect, useCallback } from "react";
import * as d3 from "d3";
import type { LineChartConfig } from "../../types/index.js";
import {
	SERIES_COLOURS,
	positionTooltip,
	formatNumber,
	useResizeObserver,
} from "./chartUtils.js";
import { useRenderLoopGuard } from "../../hooks/useRenderLoopGuard.js";

interface LineChartViewProps {
	config: LineChartConfig;
	isModal?: boolean;
}

const MARGINS = { top: 20, right: 20, bottom: 40, left: 55 };
const CHART_HEIGHT = 300;

export default function LineChartView({ config, isModal = false }: LineChartViewProps) {
	useRenderLoopGuard("LineChartView");
	const svgRef = useRef<SVGSVGElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const { width: containerWidth, height: containerHeight } = useResizeObserver(containerRef);

	const { data, series, xDataKey, xAxisLabel, yAxisLabel } = config;

	const renderChart = useCallback(() => {
		if (!svgRef.current || !data || data.length === 0 || containerWidth === 0) return;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();

		const width = containerWidth;
		// Use container height only when explicitly in modal context (isModal=true).
		// For inline charts the container height is content-driven, so using it
		// would create a feedback loop: SVG grows → container grows → SVG grows…
		const effectiveHeight = isModal ? containerHeight : 0;
		const height = effectiveHeight > 0 ? Math.min(Math.max(CHART_HEIGHT, effectiveHeight), 600) : CHART_HEIGHT;
		const innerW = width - MARGINS.left - MARGINS.right;
		const innerH = height - MARGINS.top - MARGINS.bottom;

		svg
		.attr("width", width)
		.attr("height", height)           // intrinsic size for SVG export
		.style("height", `${height}px`)   // CSS override: beats initial height:0 on SVG element
		.attr("role", "img");

		// Defs for gradients
		const defs = svg.append("defs");

		const g = svg.append("g").attr("transform", `translate(${MARGINS.left},${MARGINS.top})`);

		// X scale — treat xDataKey values as categorical band scale
		const xDomain = data.map((d) => String(d[xDataKey]));
		const xScale = d3
			.scalePoint()
			.domain(xDomain)
			.range([0, innerW])
			.padding(0.1);

		// Y scale across all series
		const allValues = series.flatMap((s) => data.map((d) => Number(d[s.dataKey]) || 0));
		const yMin = Math.min(0, d3.min(allValues) ?? 0);
		const yMax = d3.max(allValues) ?? 0;
		const yScale = d3.scaleLinear().domain([yMin, yMax * 1.1]).nice().range([innerH, 0]);

		// Grid lines
		g.append("g")
			.attr("class", "chart-grid")
			.call(
				d3.axisLeft(yScale)
					.tickSize(-innerW)
					.tickFormat(() => "")
					.ticks(5)
			)
			.select(".domain")
			.remove();

		g.selectAll(".chart-grid .tick line")
			.attr("stroke", "#e5e7eb")
			.attr("stroke-dasharray", "3,3");

		// X axis
		g.append("g")
			.attr("transform", `translate(0,${innerH})`)
			.call(d3.axisBottom(xScale).tickSizeOuter(0))
			.selectAll("text")
			.attr("font-family", "var(--font-body)")
			.attr("font-size", "11px")
			.attr("fill", "#6b7280");

		// Y axis
		g.append("g")
			.call(d3.axisLeft(yScale).ticks(5).tickFormat((v) => formatNumber(Number(v))))
			.selectAll("text")
			.attr("font-family", "var(--font-body)")
			.attr("font-size", "11px")
			.attr("fill", "#6b7280");

		g.selectAll(".domain").attr("stroke", "#e5e7eb");

		// Axis labels
		if (xAxisLabel) {
			g.append("text")
				.attr("x", innerW / 2)
				.attr("y", innerH + 36)
				.attr("text-anchor", "middle")
				.attr("font-family", "var(--font-body)")
				.attr("font-size", "12px")
				.attr("fill", "#6b7280")
				.text(xAxisLabel);
		}

		if (yAxisLabel) {
			g.append("text")
				.attr("transform", "rotate(-90)")
				.attr("x", -innerH / 2)
				.attr("y", -44)
				.attr("text-anchor", "middle")
				.attr("font-family", "var(--font-body)")
				.attr("font-size", "12px")
				.attr("fill", "#6b7280")
				.text(yAxisLabel);
		}

		// Draw each series
		series.forEach((s, i) => {
			const colour = s.colour || SERIES_COLOURS[i % SERIES_COLOURS.length];
			const isDashed = i > 0;
			const gradientId = `line-gradient-${i}-${Math.random().toString(36).slice(2)}`;

			// Gradient definition
			const gradient = defs
				.append("linearGradient")
				.attr("id", gradientId)
				.attr("x1", "0")
				.attr("y1", "0")
				.attr("x2", "0")
				.attr("y2", "1");

			gradient
				.append("stop")
				.attr("offset", "0%")
				.attr("stop-color", colour)
				.attr("stop-opacity", 0.25);

			gradient
				.append("stop")
				.attr("offset", "100%")
				.attr("stop-color", colour)
				.attr("stop-opacity", 0.02);

			// Area generator
			const areaGen = d3
				.area<Record<string, string | number>>()
				.x((d) => xScale(String(d[xDataKey])) ?? 0)
				.y0(innerH)
				.y1((d) => yScale(Number(d[s.dataKey]) || 0))
				.curve(d3.curveMonotoneX);

			// Area fill
			g.append("path")
				.datum(data)
				.attr("fill", `url(#${gradientId})`)
				.attr("d", areaGen);

			// Line generator
			const lineGen = d3
				.line<Record<string, string | number>>()
				.x((d) => xScale(String(d[xDataKey])) ?? 0)
				.y((d) => yScale(Number(d[s.dataKey]) || 0))
				.curve(d3.curveMonotoneX);

			const linePath = g
				.append("path")
				.datum(data)
				.attr("fill", "none")
				.attr("stroke", colour)
				.attr("stroke-width", 2.5)
				.attr("stroke-dasharray", isDashed ? "6 4" : null)
				.attr("d", lineGen);

			// Draw-in animation (stroke-dashoffset technique)
			const totalLength = (linePath.node() as SVGPathElement)?.getTotalLength() ?? 0;
			linePath
				.attr("stroke-dasharray", `${totalLength} ${totalLength}`)
				.attr("stroke-dashoffset", totalLength)
				.transition()
				.duration(900)
				.delay(i * 150)
				.ease(d3.easeCubicOut)
				.attr("stroke-dashoffset", 0)
				.on("end", () => {
					// Restore dashed style after animation completes
					if (isDashed) {
						linePath.attr("stroke-dasharray", "6 4");
					} else {
						linePath.attr("stroke-dasharray", null);
					}
				});

			// Data point dots
			g.selectAll(`.dot-series-${i}`)
				.data(data)
				.join("circle")
				.attr("class", `dot-series-${i}`)
				.attr("cx", (d) => xScale(String(d[xDataKey])) ?? 0)
				.attr("cy", (d) => yScale(Number(d[s.dataKey]) || 0))
				.attr("r", 4)
				.attr("fill", colour)
				.attr("stroke", "#fff")
				.attr("stroke-width", 2)
				.style("cursor", "pointer")
				.on("mouseenter", function (event: MouseEvent, d) {
					d3.select(this).attr("r", 6);
					if (tooltipRef.current) {
						tooltipRef.current.innerHTML = `<strong>${s.name}</strong><br/>${String(d[xDataKey])}: ${formatNumber(Number(d[s.dataKey]) || 0)}`;
						tooltipRef.current.style.opacity = "1";
						positionTooltip(tooltipRef.current, event);
					}
				})
				.on("mousemove", function (event: MouseEvent) {
					if (tooltipRef.current) positionTooltip(tooltipRef.current, event);
				})
				.on("mouseleave", function () {
					d3.select(this).attr("r", 4);
					if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
				});
		});
	}, [data, series, xDataKey, xAxisLabel, yAxisLabel, containerWidth, containerHeight, isModal]);

	useEffect(() => {
		renderChart();
	}, [renderChart]);

	if (!data || data.length === 0) {
		return (
			<div className="chart-empty-state" role="status">
				No data available
			</div>
		);
	}

	return (
		<div ref={containerRef} style={{ position: "relative", width: "100%", height: "100%" }}>
			{/* height: 0 prevents the browser default 150px SVG height before D3 renders.
			     D3's renderChart sets .style("height") to override this once dimensions are known. */}
			<svg ref={svgRef} style={{ display: "block", width: "100%", height: 0 }} />

			{/* Legend */}
			{series.length > 1 && (
				<div className="chart-legend" aria-label="Chart legend">
					{series.map((s, i) => (
						<div key={s.dataKey} className="chart-legend-item">
							<span
								className="chart-legend-swatch"
								style={{
									background: s.colour || SERIES_COLOURS[i % SERIES_COLOURS.length],
									...(i > 0 && {
										backgroundImage: `repeating-linear-gradient(90deg, ${s.colour || SERIES_COLOURS[i % SERIES_COLOURS.length]} 0, ${s.colour || SERIES_COLOURS[i % SERIES_COLOURS.length]} 6px, transparent 6px, transparent 10px)`,
										background: "transparent",
										borderTop: `2.5px dashed ${s.colour || SERIES_COLOURS[i % SERIES_COLOURS.length]}`,
										height: "0",
										width: "20px",
										borderRadius: "0",
									}),
								}}
								aria-hidden="true"
							/>
							<span className="chart-legend-label">{s.name}</span>
						</div>
					))}
				</div>
			)}

			{/* Tooltip */}
			<div
				ref={tooltipRef}
				className="chart-tooltip"
				role="tooltip"
				aria-live="polite"
				style={{ opacity: 0 }}
			/>
		</div>
	);
}
